import { format, parseISO, differenceInYears, differenceInCalendarDays } from 'date-fns';

/**
 * Sentinel threshold — any DOB before this year is treated as "not on file"
 * (handles the 1900-01-01 placeholder written during data import)
 */
const DOB_MIN_YEAR = 1910;

/**
 * Returns true if the DOB string represents a real, known date.
 */
/**
 * Today as a yyyy-MM-dd string in the USER'S LOCAL zone — for seeding date inputs.
 *
 * Never spell this as new Date().toISOString().split('T')[0]: that is UTC "today",
 * which after 8 PM Eastern (5 PM Pacific — a normal working hour) yields TOMORROW.
 * A payment date seeded that way lands in the wrong day's revenue and A/R reporting.
 * The ratchet (scripts/check-date-idioms.js, 'naive-utc-today') holds this class at zero.
 */
export function todayLocalISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Formats a CALENDAR DATE — a value whose meaning is a DAY, not a moment.
 *
 * A `yyyy-mm-dd` stored as a Prisma `DateTime` is UTC midnight (or, for the
 * invoice form, noon UTC). `format(new Date(iso))` re-reads that instant in the
 * viewer's zone, so every negative-offset zone — i.e. every US clinic — prints
 * the PREVIOUS day. Parsing the date PART is timezone-invariant by construction:
 * "2024-12-31T00:00:00.000Z".slice(0,10) is "2024-12-31" in Niue and Kiritimati
 * alike. Proven in the DOM by e2e/specs/timezone-rendering.spec.ts.
 *
 * ⚠ NOT for instants. An appointment start, a `createdAt`, a `paidAt` stamp — a
 * real moment — must keep rendering in the viewer's zone; routing one through
 * here would introduce the very rollback this removes. Decide per value, by KIND;
 * see .claude/rules/data-model-landmines.md ("the INVERSE trap").
 *
 * Returns '' for absent/unparseable input so a caller can `||` its own fallback;
 * `formatDob` stays the right helper for a DOB (it has the "Not on file"
 * sentinel this deliberately does not).
 */
export function formatCalendarDate(
  iso?: string | null,
  fmt: string = 'MMMM dd, yyyy',
): string {
  if (typeof iso !== 'string' || !iso) return '';
  try {
    return format(parseISO(iso.slice(0, 10)), fmt);
  } catch {
    return '';
  }
}

/**
 * How many CALENDAR DAYS from today until `iso` — the countdown twin of
 * `formatCalendarDate`, and the only correct way to age a calendar value.
 *
 * WHY NOT `new Date(iso).getTime() - Date.now()`. That is the spelling every
 * "days left" badge reaches for, and it silently mixes the two planes: a
 * calendar day arrives as UTC midnight (Postgres `date`) or noon (the anchor),
 * while `Date.now()` is a real moment in the viewer's zone. Subtracting them
 * measures ELAPSED TIME and then rounds it, so the answer swings by one across
 * the day and flips "expired" on at the wrong hour — at 9 PM Eastern a
 * UTC-midnight expiry has already "passed" by 19 hours while the clinic is
 * still on the previous day. Differencing the two DAYS answers the question
 * actually being asked.
 *
 * The stored value is date-parted (`slice(0, 10)`) and never re-zoned. "Today"
 * is the day `now` falls on in `timeZone` — pass `useClinicTimezone()`: a
 * day-valued deadline lapses at the CLINIC's midnight (DATE-KINDS §2.9), and
 * that zone is the viewer's until the clinic's is known (ADR-0034).
 *
 * `now` is a parameter ONLY so a caller can pin the flip in a test without
 * moving the host clock; production always takes the default.
 *
 * Returns null for absent/unparseable input so a caller can render its own
 * "unknown" state rather than a number it made up. Negative = already past,
 * and `0` means TODAY — an expiry is valid THROUGH the day it names, so `< 0`
 * is the overdue test and `<= 0` would expire things a day early.
 */
export function calendarDaysUntil(
  iso: string | null | undefined,
  timeZone: string,
  now: number = Date.now(),
): number | null {
  if (typeof iso !== 'string' || !iso) return null;
  try {
    const target = parseISO(iso.slice(0, 10));
    if (isNaN(target.getTime())) return null;
    const today = formatInstantInZone(new Date(now), timeZone, 'yyyy-MM-dd');
    return differenceInCalendarDays(target, parseISO(today));
  } catch {
    return null;
  }
}

/*
 * `addCalendarMonths(ymd, n)` used to live here — it existed for ONE caller, the
 * Coggins quick entry's +12-month expiry pre-fill, and that pre-fill is gone
 * (the practice manager's review: validity is jurisdictional, so the field stays
 * blank until someone reads it off the certificate). Removed rather than kept
 * "in case", because a date helper with no caller is a helper nobody is holding
 * to a rule. If a real recurrence interval ever needs month math on the web,
 * bring it back from `backend/src/common/calendar-date.anchorCalendarMonths` —
 * calendar MONTHS, never `+365` days (ADR-0019 D5).
 */

export function isDobKnown(dateOfBirth?: string | null): boolean {
  if (!dateOfBirth) return false;
  try {
    const d = new Date(dateOfBirth);
    return !isNaN(d.getTime()) && d.getFullYear() >= DOB_MIN_YEAR;
  } catch {
    return false;
  }
}

/**
 * Formats a DOB for display. Returns "Not on file" for placeholder/unknown dates.
 */
export function formatDob(
  dateOfBirth?: string | null,
  fmt: string = 'MMM d, yyyy',
): string {
  if (!isDobKnown(dateOfBirth)) return 'Not on file';
  try {
    // Use parseISO so timezone shifting doesn't flip the day
    const d = parseISO(dateOfBirth!.substring(0, 10)); // take date part only
    return format(d, fmt);
  } catch {
    return 'Not on file';
  }
}

/**
 * Returns age in years, or null if DOB is unknown/placeholder.
 */
export function computeAgeYears(dateOfBirth?: string | null): number | null {
  if (!isDobKnown(dateOfBirth)) return null;
  try {
    const d = parseISO(dateOfBirth!.substring(0, 10));
    const age = differenceInYears(new Date(), d);
    // A future DOB (data-entry typo) reads as "-8 years old" — hide it instead.
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

/**
 * Returns a formatted age string like "12 yr" or "" if unknown.
 */
export function formatAge(dateOfBirth?: string | null, suffix = 'yr'): string {
  const age = computeAgeYears(dateOfBirth);
  return age !== null ? `${age} ${suffix}` : '';
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE INSTANT PLANE
 *
 * Everything above renders a CALENDAR DAY — a value whose meaning is a day, and
 * which must therefore read the SAME string in every zone. Everything below
 * renders an INSTANT — a real moment, which is *supposed* to read differently
 * per zone, and whose only correct zone is the CLINIC's (`Clinic.timezone`),
 * not the browser's.
 *
 * Getting the two backwards is the "INVERSE trap" in
 * .claude/rules/data-model-landmines.md: routing a calendar day through the
 * instant helpers re-introduces the day-rollback these files exist to remove.
 * Decide by KIND first — Docs/architecture/DATE-KINDS.md classifies every
 * DateTime column and every baselined render site.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The schema default for `Clinic.timezone` (`backend/prisma/schema.prisma`).
 * Every clinic sits on it until the column becomes settable (DATE-KINDS §3.3),
 * so it is also the honest fallback while the clinic record is still loading.
 */
export const DEFAULT_CLINIC_TIMEZONE = 'America/New_York';

/**
 * The date-fns patterns the instant renders actually use, named so the burndown
 * is find-and-replace rather than a per-site judgement call.
 *
 * What the 85 baselined INSTANT sites spell today -> what to use:
 *   format(new Date(x), 'MMM d, yyyy h:mm a')  -> INSTANT_FORMATS.dateTime
 *   format(new Date(x), 'MMMM d, yyyy h:mm a') -> INSTANT_FORMATS.dateTimeLong
 *   format(new Date(x), 'MMM d, yyyy')         -> INSTANT_FORMATS.date
 *   new Date(x).toLocaleDateString()           -> INSTANT_FORMATS.dateNumeric
 *   new Date(x).toLocaleTimeString()           -> INSTANT_FORMATS.time
 *   new Date(x).toLocaleString()               -> INSTANT_FORMATS.dateTimeNumeric
 */
export const INSTANT_FORMATS = {
  /** "Aug 21, 2026 9:30 PM" — the default for a moment with a time of day. */
  dateTime: 'MMM d, yyyy h:mm a',
  /** "August 21, 2026 9:30 PM" */
  dateTimeLong: 'MMMM d, yyyy h:mm a',
  /** "Aug 21, 2026" — the instant's DAY *in the clinic zone*. Not a calendar day. */
  date: 'MMM d, yyyy',
  /** "8/21/2026" — the `toLocaleDateString()` shape. */
  dateNumeric: 'M/d/yyyy',
  /** "9:30 PM" — time only. */
  time: 'h:mm a',
  /** "8/21/2026, 9:30:00 PM" — the `toLocaleString()` shape. */
  dateTimeNumeric: 'M/d/yyyy, h:mm:ss a',
} as const;

/**
 * The wall-clock parts of `at` as seen in `tz`, returned as a Date whose LOCAL
 * components equal them. Purely an input to `format()` — its absolute value is
 * meaningless, so it must never be posted, stored, or compared to another Date.
 *
 * (This is what `date-fns-tz`'s `utcToZonedTime` does. `date-fns-tz` is not a
 * dependency of this app and `Intl.DateTimeFormat` is already present wherever
 * the bundle runs, so it is spelled out here rather than installed.)
 */
function zonedWallClock(at: Date, tz: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // hourCycle h23 vs h24: some engines report midnight as '24'. Normalise.
  const hour = get('hour') % 24;
  return new Date(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
    // Milliseconds are zone-invariant, so carry them across directly (Intl's
    // fractionalSecondDigits is not universally available).
    at.getMilliseconds(),
  );
}

/**
 * Renders an INSTANT in a named IANA zone — the ONE sanctioned way for the web
 * app to show a moment.
 *
 * The web app has no clinic-zone plumbing today: every `format(new Date(iso))`
 * renders in the VIEWER'S browser zone, which is right for a clinician standing
 * in the clinic and wrong for remote/relief staff, and wrong near midnight for
 * everyone. Pass the clinic's zone (see `useClinicTimezone`) and the render
 * matches what the clinic's own schedulers, statements and hospital billing
 * already use server-side (`backend/src/utils/datetime.ts`).
 *
 * ⚠ NOT for calendar days. A DOB, a due date, a service date — anything whose
 * meaning is a DAY — goes through `formatCalendarDate` / `formatDob`. Routing
 * one through here re-zones it and prints the PREVIOUS day in every US zone,
 * which is exactly the bug the calendar helpers removed. Pinned in
 * `dateUtils.test.ts` ("a CALENDAR value is the WRONG input").
 *
 * ⚠ Zone-designator tokens (`z`, `zzzz`, `X`, `O`) would render the BROWSER'S
 * offset, not `tz` — the shifted Date carries no zone of its own. Use wall-clock
 * tokens only; append the zone as literal text if a label is needed.
 *
 * Returns '' for absent/unparseable input so a caller can `||` its own fallback.
 */
export function formatInstantInZone(
  value: string | Date | null | undefined,
  tz: string,
  pattern: string = INSTANT_FORMATS.dateTime,
): string {
  if (value === null || value === undefined || value === '') return '';
  const at = value instanceof Date ? value : new Date(value);
  if (isNaN(at.getTime())) return '';
  try {
    return format(zonedWallClock(at, tz), pattern);
  } catch {
    // An invalid IANA id makes Intl throw. A clinic row can carry anything, and
    // a bad zone must not blank a clinical screen — fall back to the schema
    // default rather than to the browser's zone, which is the thing being fixed.
    try {
      return format(zonedWallClock(at, DEFAULT_CLINIC_TIMEZONE), pattern);
    } catch {
      return '';
    }
  }
}

/**
 * The zone this browser is in, e.g. `America/Denver`.
 *
 * Wrapped because `Intl.DateTimeFormat().resolvedOptions().timeZone` is
 * undefined on a handful of old engines and can throw behind a locked-down
 * `Intl`; a date render must never be the thing that blanks a clinical screen.
 * Falls back to the schema default so callers always get a real IANA id.
 */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_CLINIC_TIMEZONE;
  } catch {
    return DEFAULT_CLINIC_TIMEZONE;
  }
}

/**
 * The spellings that NAME a day and carry no zone of their own: a bare
 * `yyyy-MM-dd`, this codebase's anchor with or without the milliseconds, and any
 * EXACT hour with zero minutes/seconds that carries milliseconds.
 *
 * ⚠ That last class is the **legacy web midnight** and it is a calendar day, not
 * an instant. Before the W10 fix this section's MUI `DatePicker` handed axios a
 * `Date` at the BROWSER's midnight, serialized by `toISOString()`: `T04:00:00.000Z`
 * in EDT, `T05:00Z` in EST, `T07:00Z` in PDT, `T00:00Z` for a UTC browser. The web
 * has only ever written a DAY into this field — the picker is date-only, there is
 * no time input on the screen. The discriminator is the MILLISECONDS: iOS's
 * `ISO8601DateFormatter` never emits fractional seconds, so `.000Z` identifies the
 * web as the writer. Stated assumption: those rows are **US-written**, i.e. whole
 * hours BEHIND UTC, so the date part is the day that was picked. A half-hour zone
 * (`T18:30:00.000Z`) has non-zero minutes and never matches; a positive-offset one
 * (`T22:00:00.000Z`) would take the previous day — accepted, because the clinics
 * are US ones and the alternative is reading every legacy row as a moment.
 *
 * The same set, spelled the same way, in all three places that decide it:
 * `backend/src/common/soap-plan-dates.ts` (the write), the data migration
 * `20260902130000_anchor_soap_followup_calendar_day` (the stored rows), and here
 * (the read). Change one and you change all three, never one alone.
 */
const ZONE_FREE_DAY = /^\d{4}-\d{2}-\d{2}(?:T(?:12:00:00Z|\d{2}:00:00\.000Z))?$/;

/**
 * An ISO-ish leading `yyyy-MM-dd`. Anything without one is FREE TEXT, and free
 * text must render '' rather than a date nobody wrote: `new Date('Sep 15')` is a
 * valid Date in every browser and lands in **2001**, so a note whose follow-up
 * says "Sep 15" would print "September 15, 2001" with total confidence.
 */
const ISO_ISH = /^\d{4}-\d{2}-\d{2}/;

/**
 * Render a value that is *meant* as a DAY but may not have arrived as one —
 * today, exactly one field: `SoapNote.plan.followUpDate`.
 *
 * `plan` is a JSON blob stored verbatim, so its follow-up day carries whichever
 * spelling its writer chose. The server re-spells the ZONE-FREE ones onto the
 * one anchor (`backend/src/common/soap-plan-dates.ts`) and stores anything else
 * untouched, because a day must never be reconstructed from a zone the server
 * only assumes. That leaves exactly two populations on the wire, and this helper
 * is the whole read rule for them:
 *
 *   `2026-09-12T12:00:00Z` / `2026-09-12`  a DAY  -> its date PART, never re-zoned
 *   `2026-09-12T04:00:00.000Z`             a DAY (legacy web midnight) -> date PART
 *   `2026-09-13T01:15:00Z`                 a MOMENT -> the day it falls on HERE
 *   `next tuesday` / `Sep 15`              FREE TEXT -> `''`, never a guess
 *
 * The second arm is an older iOS build (DATE-KINDS §2.5 W8): a date-only picker
 * bound to a value that kept its time of day, posted whole. There is no way to
 * recover the day the vet tapped from the instant alone — but rendering it in
 * the viewer's own zone is right for a vet standing in the clinic that wrote it,
 * and is the same thing every un-migrated screen in this app already does with a
 * moment. Date-parting it instead would print the NEXT day for every evening
 * entry, which is the bug the campaign found.
 *
 * `browserZone` is an argument rather than a call inside, so the behaviour is
 * testable without moving the host's clock — pass `browserTimeZone()`.
 *
 * Returns '' for absent/unparseable input so a caller can `||` its own fallback.
 * Pass `'yyyy-MM-dd'` as `fmt` to get the resolved DAY back for arithmetic.
 */
export function formatCalendarOrLocalDay(
  value: string | Date | null | undefined,
  browserZone: string,
  fmt: string = INSTANT_FORMATS.date,
): string {
  if (typeof value === 'string') {
    if (ZONE_FREE_DAY.test(value)) return formatCalendarDate(value, fmt);
    // Free text takes NEITHER arm. `new Date()` accepts far more than ISO — a
    // stored "Sep 15" parses to September 15, **2001** — and a fabricated year
    // printed as a follow-up date is worse than no date at all.
    if (!ISO_ISH.test(value)) return '';
  }
  return formatInstantInZone(value, browserZone, fmt);
}

/**
 * The spellings that NAME a day, carry no zone, and could only have come from a
 * DAY-PICKER or a date-only external string.
 *
 * TIGHTER than `ZONE_FREE_DAY` above, and deliberately so: that field
 * (`SoapNote.plan.followUpDate`) has a legacy population of browser midnights at
 * ANY exact hour, because the web was its only day-writer. The columns THIS set
 * serves — `Payment.paymentDate`, `LabResult.reportDate` — really do carry
 * provider-stamped MOMENTS (a Stripe card charge, an IDEXX `orderReceivedDate`),
 * so an "any exact hour" match would misread a 9 PM charge as a day. Only three
 * spellings qualify:
 *
 *   `2026-09-03T12:00:00(.000)?Z`  the noon anchor — what every dialog now posts
 *                                  (`ymdToNoonUtcIso`) and what the server parks
 *                                  a picked `yyyy-MM-dd` on
 *                                  (`backend/src/common/payment-date.ts`)
 *   `2026-09-03T00:00:00.000Z`     the HISTORICAL spelling. Every manual payment
 *                                  and every hand-entered lab report already in
 *                                  the database is stored this way, because the
 *                                  write was `new Date('2026-09-03')` = UTC
 *                                  midnight. It is the whole reason no data
 *                                  migration is needed for either column.
 *   a bare `2026-09-03`            an optimistic offline replica row, or a
 *                                  date-only string an integration sent
 *
 * ⚠ THE DISCRIMINATOR IS THE UTC TIME-OF-DAY, NOT THE MILLISECOND, and the two
 * arms are deliberately ASYMMETRIC about the digits: the noon arm accepts
 * `T12:00:00Z` and `T12:00:00.000Z`, the midnight arm accepts only
 * `T00:00:00.000Z`. That asymmetry decides nothing for anything read back from
 * this API. `Payment.paymentDate`, `LabResult.reportDate`,
 * `MedicalRecord.date` and `Vaccination.dateAdministered` are Prisma `DateTime`
 * columns; a controller hands the Prisma object straight to `res.json`, so every
 * value leaves through `Date.prototype.toJSON` → `toISOString()` and ALWAYS
 * carries three fractional digits, whatever the client posted. `T00:00:00Z` is
 * not observable here. The optional `.000` exists for values that did NOT come
 * through the API — a hand-built string, an offline row — and the midnight arm
 * keeps its `.000` because widening it would swallow one more spelling for no
 * reachable gain. **Do not read either arm as "iOS values are excluded because
 * they lack milliseconds"; they are not.** (`StableTrackTests/DateKindsTests.
 * swift:121-124` pins the same conclusion on the mobile side.)
 *
 * SO THIS IS A TIE-BREAK WITH A RESIDUAL, not a partition of disjoint
 * populations. A real MOMENT that lands on exactly 00:00:00 or 12:00:00 UTC — a
 * whole hour, minute AND second — is read as a day and rendered from its date
 * part. Those two UTC hours are 8 PM and 8 AM Eastern (EDT; 7 PM / 7 AM in EST),
 * 5 PM and 5 AM Pacific (PDT), and the corresponding pair in every other
 * whole-hour-offset zone. The tie breaks the safe way for the population that is
 * misread on EVERY render without this rule: for every clinic this product has,
 * the clinic-zone day of UTC midnight is the PREVIOUS day — the rollback. The
 * noon boundary is safer still, because noon UTC is the same calendar day in
 * every US zone either way.
 *
 * A caller whose column has a writer that produces day-shaped MOMENTS on round
 * human-chosen minutes must exclude that writer BEFORE consulting the shape —
 * see `pages/ClinicalRecords/chartDates.medicalRecordDateKind` and its
 * `visit_note` clause.
 *
 * ⚠ AN OFFSET-BEARING SPELLING IS NOT IN THIS SET, ON PURPOSE.
 * `2026-04-29T00:00:00+14:00` names a moment, not a day — no writer in this
 * codebase produces one (Prisma serialises `Z`, and every client posts
 * `toISOString()`), and treating it as a day would be reading a zone the value
 * itself denies.
 *
 * The same set, spelled the same way, in both places that decide it: here (the
 * web read) and `backend/src/common/payment-date.ts` (the write + the PDF /
 * email / assistant reads). Change one and you change both, never one alone.
 */
const ZONE_FREE_ANCHORED_DAY =
  /^\d{4}-\d{2}-\d{2}(?:T(?:12:00:00(?:\.000)?Z|00:00:00\.000Z))?$/;

/**
 * Render one value from a column that is MIXED PER ROW between a picked DAY and
 * a real MOMENT, by the SHAPE the row carries — the sanctioned resolution for
 * that class (DATE-KINDS §0, "make the write side spell the day, then let the
 * read side split on it").
 *
 * Two live columns take it, for the same reason:
 *
 *   `Payment.paymentDate`   staff pick a DAY on "Date Received"; Stripe,
 *                           statement settlement and credit application stamp a
 *                           MOMENT. (`formatPaymentDay`, below.)
 *   `LabResult.reportDate`  this panel's own `type="date"` field posts a DAY;
 *                           the IDEXX poller writes
 *                           `parseIsoOrNow(orderReceivedDate ?? updatedDate)` —
 *                           whatever the provider sent.
 *
 * A single helper is wrong for one population either way —
 *
 *   `formatCalendarDate` everywhere → a 9 PM Eastern card charge (01:00Z the
 *   next day) moves into the NEXT day's revenue, and an evening lab result
 *   reads a day late;
 *   `formatInstantInZone` everywhere → a picked September 3 reads as
 *   "Sep 02, 2026" for every negative-offset clinic. THAT IS THE REPORTED BUG.
 *
 * So the ROW decides, by its spelling (`ZONE_FREE_ANCHORED_DAY`): a day is read
 * from its date PART and never re-zoned; anything else is the moment it is, read
 * in `zone`. **The rule is right in both readings of an unknown provider value**
 * — if the stamp carries a time it is a moment and takes the instant arm, and if
 * it is date-only it parses to exact midnight, which IS a day. That is what lets
 * a column be fixed without first observing an integration's wire shape, and it
 * is why provenance (`source === 'IDEXX'`) is the WRONG discriminator: a poller
 * that sends date-only strings writes days, and one that sends stamps writes
 * moments, sometimes in the same feed.
 *
 * `zone` is an argument rather than a hook call inside, so the behaviour is
 * testable without moving the host's clock — pass `useClinicTimezone()` (the
 * clinic's zone once it is KNOWN, the viewer's until then; ADR-0034).
 *
 * `dayFmt` defaults to `instantFmt`, which is what every caller wants; pass it
 * only when the two arms must be spelled differently (e.g. a day with no time
 * beside a moment that shows one).
 *
 * Returns '' for absent/unparseable input so a caller can `||` its own fallback.
 */
export function formatAnchoredDayOrInstant(
  value: string | Date | null | undefined,
  zone: string,
  instantFmt: string = INSTANT_FORMATS.date,
  dayFmt: string = instantFmt,
): string {
  if (value === null || value === undefined || value === '') return '';
  const iso = value instanceof Date ? value.toISOString() : value;
  if (isAnchoredDaySpelling(iso)) return formatCalendarDate(iso, dayFmt);
  return formatInstantInZone(value, zone, instantFmt);
}

/**
 * Does this value carry a spelling that NAMES a day (`ZONE_FREE_ANCHORED_DAY`)?
 *
 * The same rule `formatAnchoredDayOrInstant` renders with, exposed as a
 * predicate for the one caller that needs the KIND rather than the string:
 * `pages/ClinicalRecords/chartDates.medicalRecordDateKind`, which must combine
 * the shape with a per-TYPE override before it can pick an arm (a `visit_note`
 * is an instant even when its spelling looks like a day — 8:00 PM EDT
 * `toISOString()`s to exactly `T00:00:00.000Z`).
 *
 * Kept here, beside the regex, so the spelling set still has ONE definition.
 */
export function isAnchoredDaySpelling(value: string | Date | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false;
  const iso = value instanceof Date ? value.toISOString() : value;
  return ZONE_FREE_ANCHORED_DAY.test(iso);
}

/**
 * Render one `Payment.paymentDate` — the canonical MIXED column (DATE-KINDS
 * §1.2 / §2.4 / W6), by its per-row rule.
 *
 * A named delegate rather than a call site, because this column is the worked
 * example the doctrine points at and its behaviour is pinned by
 * `InvoiceDetail.test.tsx`, `e2e/specs/payment-date.spec.ts` and the backend
 * twin in `backend/src/common/payment-date.ts`. Keep the name; the rule it
 * delegates to is shared with `LabResult.reportDate`.
 */
export function formatPaymentDay(
  value: string | Date | null | undefined,
  zone: string,
  fmt: string = INSTANT_FORMATS.date,
): string {
  return formatAnchoredDayOrInstant(value, zone, fmt);
}

/**
 * The `yyyy-MM-dd` day a value BELONGS TO, by its per-row shape.
 *
 * `formatAnchoredDayOrInstant` answers "what should this look like on screen";
 * this answers "which day is it", which is what a COMPARISON needs. Same rule,
 * same two arms: a value spelled as an anchored day keeps its date part and is
 * never re-zoned (the INVERSE trap); anything else is a real moment, and the
 * day it belongs to is the one it falls on in `timeZone`.
 *
 * Returns '' for absent/unparseable input.
 */
export function calendarDayKey(
  value: string | Date | null | undefined,
  timeZone: string,
): string {
  if (value === null || value === undefined || value === '') return '';
  const iso = value instanceof Date ? value.toISOString() : value;
  if (isAnchoredDaySpelling(iso)) return iso.slice(0, 10);
  return formatInstantInZone(value, timeZone, 'yyyy-MM-dd');
}

/**
 * Is the calendar day `value` STRICTLY BEFORE today in `timeZone`?
 *
 * The web twin of `backend/src/common/calendar-date.isCalendarDayBeforeToday`,
 * and it must stay a twin: the two answer the same question for the same Rx,
 * one to enable a button and one to allow the request behind it. If they
 * disagree the button opens a dialog the API then rejects — or, as shipped
 * until this change, BOTH were wrong in the same direction, which is the only
 * reason nobody saw a mismatch.
 *
 * What was wrong: `new Date(expirationDate) < new Date()` compares a day
 * against a MOMENT. `Medication.expirationDate` is a calendar day parked at
 * noon UTC (DATE-KINDS §1.2), so an Rx expiring today went "expired" just
 * after 12:00Z — 8 AM Eastern — and the Dispense button went dead for the rest
 * of the clinic's working day on a prescription that had not yet expired.
 * A day-valued deadline is inclusive of its own day, in the CLINIC's zone.
 *
 * Returns `false` for an absent day: no expiry means never expired.
 *
 * `now` is injectable so tests can pin the moment rather than the host clock.
 */
export function isCalendarDayBeforeToday(
  value: string | Date | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): boolean {
  const day = calendarDayKey(value, timeZone);
  if (!day) return false;
  // Both sides are `yyyy-MM-dd`, so lexicographic order IS chronological order.
  return day < formatInstantInZone(now, timeZone, 'yyyy-MM-dd');
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE CALENDAR WRITE SIDE — one anchor, not four
 *
 * DATE-KINDS.md §2.5: four anchors are live for values that all mean "a day" —
 * noon UTC (sanctioned), UTC midnight (barn ops, DOB, reminders), browser-local
 * midnight (every MUI DatePicker — W10) and device-local midnight (iOS — W8).
 * Noon UTC is the only one with slack in BOTH directions — ±12 h, i.e. correct
 * for every offset from UTC-12 to UTC+11:59, which covers every clinic this
 * product has. (Nothing covers UTC+12..+14; there the READER closes the gap,
 * because `formatCalendarDate` never re-zones at all.) The two helpers below are
 * the only spellings a client should use to post a calendar day.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A `yyyy-MM-dd` day -> the noon-UTC ISO instant that stores it.
 *
 * `new Date('2026-01-15').toISOString()` is UTC MIDNIGHT — slack in one
 * direction only. Any render that re-zones it shows the PREVIOUS day in every
 * negative-offset zone (the live rollback bug), and a picker at browser-local
 * midnight past UTC+0 stores the previous day outright. It also leaves the
 * column carrying two anchors depending on which form wrote the row. Noon UTC
 * has 12 h of slack either way. Matches `anchorCalendarDayUtc` on the backend
 * (`backend/src/common/calendar-date.ts`) and the invoice form's own
 * `format(d, "yyyy-MM-dd'T'12:00:00.000'Z'")`.
 *
 * Returns '' for input that is not a `yyyy-MM-dd` prefix, so a caller can `||`.
 */
export function ymdToNoonUtcIso(ymd?: string | null): string {
  if (typeof ymd !== 'string') return '';
  const day = ymd.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  return `${day}T12:00:00.000Z`;
}

/**
 * A MUI `<DatePicker>` value -> the noon-UTC ISO string to POST for a CALENDAR
 * field. **The sanctioned pattern for W10.**
 *
 * A `DatePicker` hands back a `Date` at the BROWSER'S midnight, and axios
 * serialises it with `toISOString()`. For a US clinic that is `T04:00Z`/`T05:00Z`
 * — the same UTC day, benign today — but it is a fourth anchor, and for any
 * positive-offset user it rolls the day back AT STORAGE TIME, where no render
 * fix can recover it. Read the picker's LOCAL y/m/d (what the user actually
 * picked) and anchor it at noon UTC.
 *
 * ⚠ Only for CALENDAR fields. A `DateTimePicker` collecting a real moment (a
 * visit time, an admission) is an INSTANT — post `date.toISOString()` unchanged.
 *
 * Returns undefined for an empty/invalid picker value, so an optional field can
 * be omitted from the payload rather than posted as ''.
 */
export function datePickerToCalendarValue(
  value: Date | null | undefined,
): string | undefined {
  if (!(value instanceof Date) || isNaN(value.getTime())) return undefined;
  return ymdToNoonUtcIso(format(value, 'yyyy-MM-dd'));
}
